import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

const LOGO = "https://lirp.cdn-website.com/b68b7c9b/dms3rep/multi/opt/1-edeac864-1920w.png";
const LOGO_ICON = "https://lirp.cdn-website.com/b68b7c9b/dms3rep/multi/opt/ADJ_CARGIDES_ICONS_ORANJE-1920w.png";

const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "Diensten", href: "#diensten" },
  { label: "Waarom Cargides", href: "#waarom" },
  { label: "Over Ons", href: "#over-ons" },
  { label: "Referenties", href: "#referenties" },
  { label: "Jobs", href: "#jobs" },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Text colors swap depending on whether we're over the dark hero video or scrolled into white content
  const textColor = scrolled ? "text-cgdark" : "text-white";
  const ctaBg = scrolled
    ? "bg-cgdark text-white hover:bg-cgprimary"
    : "bg-white text-cgdark hover:bg-cgprimary hover:text-white";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-cgborder/60 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between">
        <a href="#home" data-testid="header-logo-link" className="flex items-center gap-3">
          {/* Use white-friendly logo over dark hero, regular logo on white bg */}
          {scrolled ? (
            <img
              src={LOGO}
              alt="Cargides Group"
              className="h-9 md:h-10 w-auto object-contain"
              data-testid="header-logo"
            />
          ) : (
            <div className="flex items-center gap-3" data-testid="header-logo-light">
              <img src={LOGO_ICON} alt="" className="h-9 md:h-10 w-auto object-contain" />
              <span className="font-heading text-white font-bold tracking-[0.2em] text-lg md:text-xl">
                CARGIDES
              </span>
            </div>
          )}
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`link-underline text-[13px] font-semibold tracking-[0.12em] uppercase transition-colors duration-500 ${textColor}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#contact"
            data-testid="header-cta-button"
            className={`hidden md:inline-flex items-center gap-2 px-5 py-3 text-[12px] font-semibold tracking-[0.15em] uppercase transition-colors duration-500 ${ctaBg}`}
          >
            Offerte aanvragen
          </a>
          <button
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden p-2 -mr-2 ${textColor}`}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 ${
          open ? "max-h-[520px] border-t border-cgborder/60 bg-white" : "max-h-0"
        }`}
      >
        <div className="px-6 py-6 flex flex-col gap-5">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="text-[14px] font-semibold tracking-[0.1em] uppercase text-cgdark"
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={() => setOpen(false)}
            className="mt-2 inline-flex items-center gap-2 bg-cgprimary text-white px-5 py-3 text-[12px] font-semibold tracking-[0.15em] uppercase"
            data-testid="mobile-cta-button"
          >
            Offerte aanvragen
          </a>
        </div>
      </div>
    </header>
  );
}
